/**
 * Maintenance state — set by the API layer, read by the app shell.
 *
 * WHAT IT IS NOT. This is not the gate. The gate is Caddy returning 503 for
 * /v1/* on the host; that is what actually stops a transaction. A client flag
 * cannot: `/v1/` is denylisted from the service worker's caches, so a stale app
 * shell still reaches the network, and a live session token plus curl bypasses
 * the UI entirely. This module only makes the server's answer legible — it
 * explains, it does not enforce.
 *
 * WHY A MODULE-LEVEL STORE rather than context or a query cache: the signal
 * originates in `api.ts`, which is plain functions with no React scope, and it
 * has to be readable from the app shell without threading a provider through
 * every call site. One boolean, one listener set, no dependencies.
 *
 * DELIBERATELY ONE-WAY. Nothing here clears the flag. Recovery is a reload (see
 * `Maintenance.tsx`), because the honest way to learn the window has ended is
 * to boot the app again and let its normal requests answer the question. A
 * self-clearing poll would need an endpoint the gate does NOT cover — and every
 * such endpoint is one the gate deliberately leaves open (/health is probed by
 * the load balancer), so a probe against it would report "back up" while /v1 is
 * still refusing. That is the wrong answer delivered confidently.
 */

let inMaintenance = false;
const listeners = new Set<(value: boolean) => void>();

/** Whether the server has told us it is in maintenance. */
export function isMaintenance(): boolean {
  return inMaintenance;
}

/**
 * Record that the server answered with a maintenance status.
 *
 * Idempotent: repeated 503s from concurrent in-flight requests notify once, so
 * a dashboard firing several calls at boot does not re-render the shell per
 * response.
 */
export function reportMaintenance(): void {
  if (inMaintenance) return;
  inMaintenance = true;
  for (const listener of listeners) listener(true);
}

/** Subscribe to changes. Returns an unsubscribe function. */
export function subscribeMaintenance(listener: (value: boolean) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only reset. Not part of the runtime contract. */
export function resetMaintenanceForTests(): void {
  inMaintenance = false;
  listeners.clear();
}
